/**
 * AdminSpiritualDirection, seção do painel admin (Iter 34, Módulo 3).
 *
 * Compõe sem tocar em AdminWeeklyThemes.jsx: lê o tema ATIVO e edita apenas
 * os 5 campos novos da Direção Espiritual + oferece a busca de conteúdo
 * alinhado (top 5 sermões e planos).
 *
 * Se não houver tema ativo, mostra estado vazio com CTA suave (o admin cria
 * o tema no componente existente AdminWeeklyThemes, aqui só complementa).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Compass, Search, Save, BookOpen, Layers, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

const PUBLICO = [
  { v: "familia", label: "Família" },
  { v: "jovens", label: "Jovens" },
  { v: "todos", label: "Todos" },
  { v: "lideranca", label: "Liderança" },
];
const TOM = [
  { v: "esperanca", label: "Esperança" },
  { v: "conviccao", label: "Convicção" },
  { v: "consolo", label: "Consolo" },
  { v: "desafio", label: "Desafio" },
  { v: "celebracao", label: "Celebração" },
  { v: "reflexao", label: "Reflexão" },
];
const BASE = [
  { v: "reformada", label: "Reformada" },
  { v: "historica", label: "Histórica" },
  { v: "evangelistica", label: "Evangelística" },
  { v: "patristica", label: "Patrística" },
  { v: "wesleyana", label: "Wesleyana" },
];


function fmtDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return iso;
  }
}


export default function AdminSpiritualDirection() {
  const [active, setActive] = useState(null); // tema ativo hoje
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [form, setForm] = useState({
    objetivo_espiritual: "",
    palavra_chave: "",
    publico_alvo: "",
    tom_pastoral: "",
    base_teologica: "",
  });

  // Busca de conteúdo alinhado
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/weekly-theme/current");
      const t = data?.theme || null;
      setActive(t);
      if (t) {
        setForm({
          objetivo_espiritual: t.objetivo_espiritual || "",
          palavra_chave: t.palavra_chave || "",
          publico_alvo: t.publico_alvo || "",
          tom_pastoral: t.tom_pastoral || "",
          base_teologica: t.base_teologica || "",
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao carregar tema ativo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!active?.id) return;
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const payload = {
        objetivo_espiritual: form.objetivo_espiritual.trim() || null,
        palavra_chave: form.palavra_chave.trim() || null,
        publico_alvo: form.publico_alvo || null,
        tom_pastoral: form.tom_pastoral || null,
        base_teologica: form.base_teologica || null,
      };
      const { data } = await api.put(`/admin/weekly-themes/${active.id}`, payload);
      setActive(data);
      setOk("Direção espiritual atualizada.");
      // Busca automática se palavra-chave preenchida
      if (payload.palavra_chave) {
        setSearchTerm(payload.palavra_chave);
        await runSearch(payload.palavra_chave);
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Falha ao salvar. Verifique os campos.");
    } finally {
      setSaving(false);
    }
  };

  const runSearch = async (term) => {
    const q = (term ?? searchTerm).trim();
    if (q.length < 2) {
      setErr("Palavra-chave precisa ter pelo menos 2 letras.");
      return;
    }
    setSearching(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/weekly-themes/search-content", {
        params: { palavra_chave: q, limit: 5 },
      });
      setSearchResults(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha na busca.");
    } finally {
      setSearching(false);
    }
  };

  const stats = useMemo(() => {
    if (!active) return null;
    return {
      tema: active.theme_name || "Sem tema definido",
      periodo: `${fmtDate(active.week_start)} a ${fmtDate(active.week_end)}`,
      versiculo: active.verse_reference || "-",
      palavra: active.palavra_chave || "-",
      objetivo: active.objetivo_espiritual || "-",
      status: active.status || "ativo",
      priority: active.priority || "normal",
      program_paused: !!active.program_paused,
    };
  }, [active]);

  const setStatus = async (newStatus) => {
    if (!active?.id) return;
    try {
      const { data } = await api.post(
        `/admin/weekly-themes/${active.id}/set-status?status=${newStatus}`
      );
      setActive(data);
      setOk(`Status alterado para ${newStatus}.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao alterar status.");
    }
  };

  const togglePause = async () => {
    if (!active?.id) return;
    try {
      const { data } = await api.post(
        `/admin/weekly-themes/${active.id}/pause?paused=${!active.program_paused}`
      );
      setActive(data);
      setOk(data.program_paused ? "Programação pausada." : "Programação retomada.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao alternar pausa.");
    }
  };

  const setEmergencial = async () => {
    if (!active?.id) return;
    if (!window.confirm("Marcar este tema como EMERGENCIAL? Ele terá prioridade máxima e sobrescreverá qualquer outro tema ativo.")) return;
    try {
      const { data } = await api.put(`/admin/weekly-themes/${active.id}`, {
        priority: "emergencial",
      });
      setActive(data);
      setOk("Tema marcado como emergencial.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha.");
    }
  };

  const doRollback = async () => {
    if (!active?.id) return;
    if (!window.confirm("Restaurar tema ao estado anterior à última alteração?")) return;
    try {
      const { data } = await api.post(`/admin/weekly-themes/${active.id}/rollback`);
      setActive(data);
      setOk("Rollback aplicado.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha no rollback.");
    }
  };

  return (
    <section
      data-testid="admin-spiritual-direction-section"
      className="bg-white rounded-3xl shadow-sm border border-[#0B1E36]/8 p-6 md:p-8 mt-8"
    >
      <header className="flex items-center gap-3 mb-5">
        <Compass className="w-5 h-5 text-[#C5A059]" />
        <div>
          <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[#0B1E36]/55">
            Módulo pastoral
          </p>
          <h2 className="font-serif-display text-2xl text-[#0B1E36]">
            Direção espiritual da semana
          </h2>
        </div>
      </header>

      {err && (
        <div
          data-testid="admin-spiritual-direction-error"
          className="mb-4 px-3 py-2 rounded bg-rose-50 border border-rose-200 text-rose-700 text-sm inline-flex items-center gap-2"
        >
          <AlertCircle className="w-4 h-4" />
          {err}
        </div>
      )}
      {ok && (
        <div
          data-testid="admin-spiritual-direction-ok"
          className="mb-4 px-3 py-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm"
        >
          {ok}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#0B1E36]/55">Carregando...</p>
      ) : !active ? (
        <div
          data-testid="admin-spiritual-direction-empty"
          className="rounded-xl border border-dashed border-[#0B1E36]/20 p-6 text-center text-[#0B1E36]/65 text-sm"
        >
          Nenhum tema ativo hoje. Programe uma semana temática na seção
          &ldquo;Semanas temáticas (Dom → Sáb)&rdquo; acima para habilitar a
          Direção Espiritual.
        </div>
      ) : (
        <>
          {/* Card estatístico */}
          <div
            data-testid="admin-spiritual-direction-stats"
            className="grid md:grid-cols-4 gap-3 mb-4 p-4 rounded-2xl bg-[#FAF8F4] border border-[#0B1E36]/10"
          >
            <StatMini label="Tema atual" value={stats.tema} testid="stat-tema" />
            <StatMini label="Período" value={stats.periodo} testid="stat-periodo" />
            <StatMini label="Texto base" value={stats.versiculo} testid="stat-versiculo" />
            <StatMini label="Palavra-chave" value={stats.palavra} testid="stat-palavra" />
          </div>

          {/* Sprint 2: painel de controle */}
          <div
            data-testid="sd-control-panel"
            className="flex flex-wrap items-center gap-2 mb-6 p-3 rounded-xl border border-[#0B1E36]/10 bg-white"
          >
            <span
              data-testid="sd-badge-status"
              className={`text-[0.7rem] uppercase tracking-wider px-2.5 py-1 rounded-full font-semibold ${
                stats.status === "ativo" ? "bg-emerald-100 text-emerald-800"
                : stats.status === "rascunho" ? "bg-slate-200 text-slate-700"
                : stats.status === "agendado" ? "bg-amber-100 text-amber-800"
                : "bg-rose-100 text-rose-800"
              }`}
            >
              {stats.status}
            </span>
            <span
              data-testid="sd-badge-priority"
              className={`text-[0.7rem] uppercase tracking-wider px-2.5 py-1 rounded-full font-semibold ${
                stats.priority === "emergencial" ? "bg-red-600 text-white" : "bg-[#0B1E36]/10 text-[#0B1E36]/70"
              }`}
            >
              {stats.priority}
            </span>
            {stats.program_paused && (
              <span
                data-testid="sd-badge-paused"
                className="text-[0.7rem] uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-200 text-amber-900 font-semibold"
              >
                pausado
              </span>
            )}
            <div className="flex-1" />
            <div className="flex flex-wrap gap-2">
              <select
                data-testid="sd-set-status"
                value={stats.status}
                onChange={(e) => setStatus(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-[#0B1E36]/15"
              >
                <option value="rascunho">rascunho</option>
                <option value="agendado">agendado</option>
                <option value="ativo">ativo</option>
                <option value="encerrado">encerrado</option>
              </select>
              <button
                type="button"
                data-testid="sd-toggle-pause"
                onClick={togglePause}
                className="text-xs px-3 py-1.5 rounded border border-[#0B1E36]/20 hover:bg-[#0B1E36]/5"
              >
                {stats.program_paused ? "Retomar" : "Pausar"}
              </button>
              <button
                type="button"
                data-testid="sd-set-emergencial"
                onClick={setEmergencial}
                disabled={stats.priority === "emergencial"}
                className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
              >
                Emergencial
              </button>
              <button
                type="button"
                data-testid="sd-rollback"
                onClick={doRollback}
                className="text-xs px-3 py-1.5 rounded border border-[#0B1E36]/20 hover:bg-[#0B1E36]/5"
              >
                Rollback
              </button>
            </div>
          </div>

          {/* Editor dos 5 campos */}
          <form
            onSubmit={submit}
            data-testid="admin-spiritual-direction-form"
            className="grid md:grid-cols-12 gap-4 mb-6"
          >
            <div className="md:col-span-12">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Objetivo espiritual (max 280)
              </label>
              <textarea
                data-testid="sd-objetivo"
                value={form.objetivo_espiritual}
                onChange={(e) =>
                  setForm({ ...form, objetivo_espiritual: e.target.value })
                }
                maxLength={280}
                rows={2}
                placeholder="Ex.: Redespertar o hábito da oração contínua no lar."
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
              />
              <p className="text-xs text-[#0B1E36]/45 mt-1">
                {form.objetivo_espiritual.length}/280
              </p>
            </div>

            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Palavra-chave (para busca)
              </label>
              <input
                type="text"
                data-testid="sd-palavra"
                value={form.palavra_chave}
                onChange={(e) => setForm({ ...form, palavra_chave: e.target.value })}
                maxLength={60}
                placeholder="Ex.: oração"
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
              />
            </div>

            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Público-alvo
              </label>
              <select
                data-testid="sd-publico"
                value={form.publico_alvo}
                onChange={(e) => setForm({ ...form, publico_alvo: e.target.value })}
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm bg-white"
              >
                <option value="">(nenhum)</option>
                {PUBLICO.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Tom pastoral
              </label>
              <select
                data-testid="sd-tom"
                value={form.tom_pastoral}
                onChange={(e) => setForm({ ...form, tom_pastoral: e.target.value })}
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm bg-white"
              >
                <option value="">(nenhum)</option>
                {TOM.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-6">
              <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
                Base teológica
              </label>
              <select
                data-testid="sd-base"
                value={form.base_teologica}
                onChange={(e) => setForm({ ...form, base_teologica: e.target.value })}
                className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm bg-white"
              >
                <option value="">(nenhuma)</option>
                {BASE.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-6 flex items-end justify-end">
              <button
                type="submit"
                disabled={saving}
                data-testid="sd-submit"
                className="px-5 py-2.5 rounded bg-[#C5A059] hover:bg-[#a88848] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {saving ? "Salvando..." : "Salvar direção espiritual"}
              </button>
            </div>
          </form>

          {/* Busca de conteúdo alinhado */}
          <div
            data-testid="admin-spiritual-direction-search"
            className="rounded-2xl border border-[#0B1E36]/10 p-4"
          >
            <div className="flex items-center gap-3 mb-3">
              <Search className="w-4 h-4 text-[#C5A059]" />
              <h3 className="text-sm font-semibold text-[#0B1E36]">
                Buscar conteúdo alinhado por palavra-chave
              </h3>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                data-testid="sd-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), runSearch())}
                placeholder="Ex.: oração, família, cruz..."
                className="flex-1 px-3 py-2 rounded border border-[#0B1E36]/15 text-sm"
              />
              <button
                type="button"
                onClick={() => runSearch()}
                disabled={searching}
                data-testid="sd-search-btn"
                className="px-4 py-2 rounded bg-[#0B1E36] hover:bg-[#122a4c] text-white text-sm inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Search className="w-4 h-4" />
                {searching ? "Buscando..." : "Buscar"}
              </button>
            </div>

            {searchResults && (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div data-testid="sd-search-sermons">
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#0B1E36]/60">
                    <BookOpen className="w-3.5 h-3.5" /> Top {searchResults.sermons_count} sermões
                  </div>
                  {searchResults.sermons.length === 0 ? (
                    <p className="text-xs text-[#0B1E36]/45">Nada alinhado.</p>
                  ) : (
                    <ul className="space-y-1">
                      {searchResults.sermons.map((s) => (
                        <li key={s.slug} className="text-sm text-[#0B1E36] leading-snug">
                          <span className="text-[#C5A059] text-xs mr-1">
                            #{s.number || "?"}
                          </span>
                          <span className="font-medium">{s.title}</span>
                          <span className="text-xs text-[#0B1E36]/50 ml-1">
                            [{s.category}]
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div data-testid="sd-search-plans">
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#0B1E36]/60">
                    <Layers className="w-3.5 h-3.5" /> Top {searchResults.plans_count} planos
                  </div>
                  {searchResults.plans.length === 0 ? (
                    <p className="text-xs text-[#0B1E36]/45">Nada alinhado.</p>
                  ) : (
                    <ul className="space-y-1">
                      {searchResults.plans.map((p) => (
                        <li key={p.slug} className="text-sm text-[#0B1E36] leading-snug">
                          <span className="font-medium">{p.title}</span>
                          <span className="text-xs text-[#0B1E36]/50 ml-1">
                            ({p.duration_days}d)
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}


function StatMini({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl bg-white border border-[#0B1E36]/10 p-3"
    >
      <p className="text-[0.65rem] uppercase tracking-wider text-[#0B1E36]/50">
        {label}
      </p>
      <p className="text-sm font-medium text-[#0B1E36] mt-1 line-clamp-2 break-words">
        {value}
      </p>
    </div>
  );
}
